import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Card,
  CardContent,
} from '@mui/material';
import StandardizedChip from '../../common/StandardizedChip';
import { DialogTitleWithClose } from '../../common';
import {
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { formatTimestamp, getSeverityColor, getSeverityIcon } from './utils';

/**
 * AuditDetailsDialog Component
 *
 * Displays detailed information about a selected audit log entry.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Callback to close dialog
 * @param {Object} props.selectedLog - Selected audit log entry
 */
function AuditDetailsDialog({ open, onClose, selectedLog }) {
  if (!selectedLog) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitleWithClose
        title={
          <Box display="flex" alignItems="center">
            <SecurityIcon sx={{ mr: 1 }} />
            Content Check Details
          </Box>
        }
        onClose={onClose}
      />

      <DialogContent>
        <Box>
          {/* Basic Info */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Reference ID
              </Typography>
              <Typography variant="body1">
                {selectedLog.reference_id}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Reference Type
              </Typography>
              <Typography variant="body1">
                {selectedLog.reference_type}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                User Email
              </Typography>
              <Typography variant="body1">
                {selectedLog.user_id || 'N/A'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Timestamp
              </Typography>
              <Typography variant="body1">
                {formatTimestamp(selectedLog.timestamp)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Risk Score
              </Typography>
              <Typography variant="body1">{selectedLog.risk_score}%</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Total Violations
              </Typography>
              <Typography variant="body1">
                {selectedLog.total_violations}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Original Content */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Original Content</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={selectedLog.original_input}
                InputProps={{ readOnly: true }}
                variant="outlined"
              />
            </AccordionDetails>
          </Accordion>

          {/* Filtered Content */}
          {selectedLog.filtered_content && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Filtered Content</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={selectedLog.filtered_content}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </AccordionDetails>
            </Accordion>
          )}

          {/* Violations */}
          {selectedLog.violations && selectedLog.violations.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  Violations ({selectedLog.violations.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {selectedLog.violations.map((violation, index) => (
                  <Card key={index} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        {getSeverityIcon(violation.severity)}
                        <StandardizedChip
                          label={violation.violation_type}
                          color={getSeverityColor(violation.severity)}
                        />
                        <StandardizedChip
                          label={violation.severity}
                          color={getSeverityColor(violation.severity)}
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {violation.description}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </AccordionDetails>
            </Accordion>
          )}

          {/* Anonymization Results */}
          {selectedLog.anonymization_results &&
            selectedLog.anonymization_results.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    Anonymization Results (
                    {selectedLog.anonymization_results.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {selectedLog.anonymization_results.map((result, index) => (
                    <Card key={index} sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          {result.entity_type}
                        </Typography>
                        <Box display="flex" gap={2}>
                          <Box flex={1}>
                            <Typography variant="body2" color="text.secondary">
                              Original:
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {result.original_text}
                            </Typography>
                          </Box>
                          <Box flex={1}>
                            <Typography variant="body2" color="text.secondary">
                              Anonymized:
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {result.anonymized_text}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default AuditDetailsDialog;
